import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AppBar, Toolbar, Typography, IconButton, Tooltip } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useTasks } from "../AppContext";
import { API_edit_booking } from "../utils/http";
import { useEffect, useState } from "react";
import { STATUS_CODE } from "../utils/constDispatch";
import { checkOper_or_admin } from "../utils/tool";
import { canEditCurrentBooking } from "../utils/bookingPermission";
import { getApiErrorMessage } from "../utils/apiError";
import { Toast } from "./Toast";

export default function NavbarSave({ title, onGoBack, booking_id }) {
  const data_pdf = useTasks();
  const [toast, set_toast] = useState(null);

  // Mirrors PUT /api/booking/:id — editers may only touch bookings they created,
  // and never a sample. Without this the request goes out and comes back 403.
  // useTasks() trả initialTasks ({_setting: ...}) chứ không phải null, nên không
  // thể lấy `data_pdf == null` làm mốc "chưa tải xong". Mốc phải là một field
  // thật của booking: `_id` không dùng được vì SaveChange xoá mọi key `_`-prefix
  // khỏi chính object state. Không có mốc này thì admin bấm Ctrl+S trong lúc
  // đang tải sẽ gửi PUT với body rỗng.
  const loading = data_pdf?.booking_number == null;
  // Sample bookings are a separate backend rule (editers are refused outright),
  // so it sits on top of the owner check rather than inside it.
  const is_sample = data_pdf?.status === STATUS_CODE.sample;
  const sample_blocked = is_sample && !checkOper_or_admin();
  const can_save =
    !loading && canEditCurrentBooking(data_pdf) && !sample_blocked;

  const blocked_reason = loading
    ? "Đang tải booking"
    : sample_blocked
      ? "Chỉ admin/operator sửa được booking sample"
      : "Booking này do người khác tạo, bạn không có quyền sửa";

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onGoBack();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onGoBack]);

  const SaveChange = () => {
    if (!can_save) {
      set_toast({ severity: "warning", text: blocked_reason });
      return;
    }

    let data_body = data_pdf;
    for (var key in data_body) {
      if (key.startsWith("_")) {
        delete data_body[key];
      }
    }
    API_edit_booking(booking_id, data_body, (data) => {
      if (data?.success) {
        set_toast({ severity: "success", text: "Đã lưu" });
      } else {
        // This used to be an empty callback: a failed save looked identical to a
        // successful one and the edits were silently lost.
        set_toast({ severity: "error", text: getApiErrorMessage(data) });
      }
    });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault();
        // Ctrl+S goes through SaveChange, which re-checks can_save — otherwise
        // the shortcut would bypass the disabled button.
        SaveChange();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [data_pdf, booking_id, can_save]);

  return (
    <AppBar position="sticky" sx={{ top: 0, zIndex: 1100 }}>
      <Toolbar>
        <IconButton onClick={onGoBack} sx={{ color: "white" }}>
          <ArrowBackIcon />
          <Typography sx={{ ml: 1 }}>Exit</Typography>
        </IconButton>

        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>

        <Tooltip title={can_save ? "" : blocked_reason}>
          {/* span wrapper: MUI disabled buttons don't fire hover events */}
          <span>
            <IconButton
              sx={{ color: "white", "&.Mui-disabled": { color: "#ffffff80" } }}
              disabled={!can_save}
              onClick={() => SaveChange()}
            >
              <SaveIcon />
              <Typography sx={{ ml: 1 }}>Save</Typography>
            </IconButton>
          </span>
        </Tooltip>
      </Toolbar>

      <Toast value={toast} onClose={() => set_toast(null)} />
    </AppBar>
  );
}
